import { apiClient } from '../../shared/apiClient';
import {
  Asset,
  MaintenanceRecord,
  AssetRequest,
} from '../../../types/entities/admin/assetManagement';
import type { AssetReservation as AssetReservationType } from '../../../types/entities/admin/assetManagement';

export interface AssetDocument {
  id: string;
  assetId: string;
  tenantId: string;
  name: string;
  type: string;
  category: 'manual' | 'warranty' | 'certification' | 'insurance' | 'photos' | 'other';
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  uploadedAt: string;
  lastAccessed?: string;
  accessCount: number;
  isPublic: boolean;
  description?: string;
  tags?: string[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssetPhoto {
  id: string;
  assetId: string;
  tenantId: string;
  url: string;
  filename: string;
  caption?: string;
  isPrimary: boolean;
  uploadedBy: string;
  uploadedAt: string;
  fileSize: number;
  mimeType: string;
  dimensions?: {
    width: number;
    height: number;
  };
  tags?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AssetReservation {
  id: string;
  assetId: string;
  tenantId: string;
  userId: string;
  userName: string;
  userEmail: string;
  startDate: string;
  endDate: string;
  purpose: string;
  status: 'pending' | 'approved' | 'rejected' | 'active' | 'completed' | 'cancelled';
  notes?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  cancellationReason?: string;
  checkInTime?: string;
  checkOutTime?: string;
  damageReported?: boolean;
  damageCost?: number;
  createdAt: string;
  updatedAt: string;
}

class AssetService {
  // Asset CRUD operations
  async getAssets(
    tenantId: string,
    filters?: {
      category?: string;
      status?: string;
      condition?: string;
      location?: string;
      search?: string;
    },
  ): Promise<Asset[]> {
    return apiClient.get<Asset[]>('/assets', { tenantId, ...filters });
  }

  async getAsset(assetId: string): Promise<Asset> {
    return apiClient.get<Asset>(`/assets/${assetId}`);
  }

  async createAsset(
    tenantId: string,
    assetData: Omit<Asset, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>,
  ): Promise<Asset> {
    return apiClient.post<Asset>('/assets', { tenantId, ...assetData });
  }

  async updateAsset(assetId: string, updates: Partial<Asset>): Promise<Asset> {
    return apiClient.put<Asset>(`/assets/${assetId}`, updates);
  }

  async deleteAsset(assetId: string): Promise<void> {
    await apiClient.delete(`/assets/${assetId}`);
  }

  // Asset reservations/bookings
  async getAssetReservations(
    tenantId: string,
    filters?: {
      assetId?: string;
      userId?: string;
      status?: string;
      startDate?: string;
      endDate?: string;
    },
  ): Promise<AssetReservation[]> {
    return apiClient.get<AssetReservation[]>('/assets/reservations', { tenantId, ...filters });
  }

  async createReservation(
    tenantId: string,
    reservationData: Omit<
      AssetReservation,
      'id' | 'tenantId' | 'status' | 'createdAt' | 'updatedAt'
    >,
  ): Promise<AssetReservation> {
    const fullReservationData = {
      tenantId,
      status: 'pending' as const,
      ...reservationData,
    };
    return apiClient.post<AssetReservation>('/assets/reservations', fullReservationData);
  }

  async updateReservation(
    reservationId: string,
    updates: Partial<AssetReservation>,
  ): Promise<AssetReservation> {
    return apiClient.put<AssetReservation>(`/assets/reservations/${reservationId}`, updates);
  }

  async cancelReservation(reservationId: string, reason: string): Promise<AssetReservation> {
    return apiClient.put<AssetReservation>(`/assets/reservations/${reservationId}/cancel`, {
      cancellationReason: reason,
    });
  }

  async approveReservation(
    reservationId: string,
    approvedBy: string,
    notes?: string,
  ): Promise<AssetReservation> {
    return apiClient.put<AssetReservation>(`/assets/reservations/${reservationId}/approve`, {
      approvedBy,
      notes,
    });
  }

  async rejectReservation(reservationId: string, reason: string): Promise<AssetReservation> {
    return apiClient.put<AssetReservation>(`/assets/reservations/${reservationId}/reject`, {
      rejectionReason: reason,
    });
  }

  async checkInAsset(reservationId: string, userId: string): Promise<AssetReservation> {
    return apiClient.put<AssetReservation>(`/assets/reservations/${reservationId}/checkin`, {
      checkedInBy: userId,
    });
  }

  async checkOutAsset(
    reservationId: string,
    userId: string,
    damageReported?: boolean,
    damageCost?: number,
  ): Promise<AssetReservation> {
    return apiClient.put<AssetReservation>(`/assets/reservations/${reservationId}/checkout`, {
      checkedOutBy: userId,
      damageReported,
      damageCost,
    });
  }

  // Asset maintenance
  async getMaintenanceRecords(
    tenantId: string,
    filters?: {
      assetId?: string;
      type?: string;
      status?: string;
      priority?: string;
      performedBy?: string;
    },
  ): Promise<MaintenanceRecord[]> {
    return apiClient.get<MaintenanceRecord[]>('/assets/maintenance', { tenantId, ...filters });
  }

  async createMaintenanceRecord(
    tenantId: string,
    maintenanceData: Omit<MaintenanceRecord, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<MaintenanceRecord> {
    return apiClient.post<MaintenanceRecord>('/assets/maintenance', {
      tenantId,
      ...maintenanceData,
    });
  }

  async updateMaintenanceRecord(
    maintenanceId: string,
    updates: Partial<MaintenanceRecord>,
  ): Promise<MaintenanceRecord> {
    return apiClient.put<MaintenanceRecord>(`/assets/maintenance/${maintenanceId}`, updates);
  }

  async deleteMaintenanceRecord(maintenanceId: string): Promise<void> {
    await apiClient.delete(`/assets/maintenance/${maintenanceId}`);
  }

  async completeMaintenanceRecord(
    maintenanceId: string,
    completionData: {
      performedBy: string;
      actualCost?: number;
      notes?: string;
      partsUsed?: string[];
    },
  ): Promise<MaintenanceRecord> {
    return apiClient.put<MaintenanceRecord>(
      `/assets/maintenance/${maintenanceId}/complete`,
      completionData,
    );
  }

  // Asset documents
  async getAssetDocuments(assetId: string, category?: string): Promise<AssetDocument[]> {
    return apiClient.get<AssetDocument[]>(`/assets/${assetId}/documents`, { category });
  }

  async uploadAssetDocument(
    assetId: string,
    file: File,
    documentData: {
      name: string;
      category: AssetDocument['category'];
      description?: string;
      tags?: string[];
      isPublic?: boolean;
    },
  ): Promise<AssetDocument> {
    return apiClient.upload<AssetDocument>(`/assets/${assetId}/documents`, file, documentData);
  }

  async updateAssetDocument(
    documentId: string,
    updates: Partial<
      Pick<AssetDocument, 'name' | 'description' | 'tags' | 'isPublic' | 'category'>
    >,
  ): Promise<AssetDocument> {
    return apiClient.put<AssetDocument>(`/assets/documents/${documentId}`, updates);
  }

  async deleteAssetDocument(documentId: string): Promise<void> {
    await apiClient.delete(`/assets/documents/${documentId}`);
  }

  async downloadAssetDocument(documentId: string): Promise<Blob> {
    const response = await fetch(
      `${apiClient['baseUrl']}/assets/documents/${documentId}/download`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  // Asset photos
  async getAssetPhotos(assetId: string): Promise<AssetPhoto[]> {
    return apiClient.get<AssetPhoto[]>(`/assets/${assetId}/photos`);
  }

  async uploadAssetPhoto(
    assetId: string,
    file: File,
    photoData: {
      caption?: string;
      isPrimary?: boolean;
      tags?: string[];
    },
  ): Promise<AssetPhoto> {
    return apiClient.upload<AssetPhoto>(`/assets/${assetId}/photos`, file, photoData);
  }

  async updateAssetPhoto(
    photoId: string,
    updates: Partial<Pick<AssetPhoto, 'caption' | 'isPrimary' | 'tags'>>,
  ): Promise<AssetPhoto> {
    return apiClient.put<AssetPhoto>(`/assets/photos/${photoId}`, updates);
  }

  async deleteAssetPhoto(photoId: string): Promise<void> {
    await apiClient.delete(`/assets/photos/${photoId}`);
  }

  async setPrimaryPhoto(assetId: string, photoId: string): Promise<AssetPhoto> {
    return apiClient.put<AssetPhoto>(`/assets/${assetId}/photos/${photoId}/primary`, {});
  }

  // Asset requests
  async getAssetRequests(
    tenantId: string,
    filters?: {
      status?: string;
      priority?: string;
      requestedBy?: string;
    },
  ): Promise<AssetRequest[]> {
    return apiClient.get<AssetRequest[]>('/assets/requests', { tenantId, ...filters });
  }

  async createAssetRequest(
    tenantId: string,
    requestData: Omit<AssetRequest, 'id' | 'tenantId' | 'status' | 'createdAt' | 'updatedAt'>,
  ): Promise<AssetRequest> {
    const fullRequestData = {
      tenantId,
      status: 'pending' as const,
      ...requestData,
    };
    return apiClient.post<AssetRequest>('/assets/requests', fullRequestData);
  }

  async updateAssetRequest(
    requestId: string,
    updates: Partial<AssetRequest>,
  ): Promise<AssetRequest> {
    return apiClient.put<AssetRequest>(`/assets/requests/${requestId}`, updates);
  }

  async approveAssetRequest(
    requestId: string,
    approvedBy: string,
    notes?: string,
  ): Promise<AssetRequest> {
    return apiClient.put<AssetRequest>(`/assets/requests/${requestId}/approve`, {
      approvedBy,
      notes,
    });
  }

  async rejectAssetRequest(requestId: string, reason: string): Promise<AssetRequest> {
    return apiClient.put<AssetRequest>(`/assets/requests/${requestId}/reject`, {
      rejectionReason: reason,
    });
  }

  // Asset availability and analytics
  async checkAssetAvailability(
    assetId: string,
    startDate: string,
    endDate: string,
  ): Promise<boolean> {
    const result = await apiClient.get<{ available: boolean }>('/assets/availability', {
      assetId,
      startDate,
      endDate,
    });
    return result.available;
  }

  async getAssetUtilization(
    assetId: string,
    startDate?: string,
    endDate?: string,
  ): Promise<{
    utilizationRate: number;
    totalBookings: number;
    totalHours: number;
    averageBookingDuration: number;
    peakUsageTimes: Array<{ hour: number; count: number }>;
  }> {
    return apiClient.get(`/assets/${assetId}/utilization`, { startDate, endDate });
  }

  async getAssetMaintenanceCosts(
    assetId: string,
    year?: number,
  ): Promise<{
    totalCost: number;
    monthlyBreakdown: Array<{ month: number; cost: number }>;
    typeBreakdown: Array<{ type: string; cost: number; count: number }>;
  }> {
    return apiClient.get(`/assets/${assetId}/maintenance-costs`, { year });
  }

  // Bulk operations
  async bulkUpdateAssets(assetIds: string[], updates: Partial<Asset>): Promise<Asset[]> {
    return apiClient.put<Asset[]>('/assets/bulk', { assetIds, updates });
  }

  async bulkDeleteAssets(assetIds: string[]): Promise<void> {
    await apiClient.post('/assets/bulk-delete', { assetIds });
  }

  async exportAssets(
    tenantId: string,
    format: 'csv' | 'xlsx' | 'pdf',
    filters?: Record<string, unknown>,
  ): Promise<Blob> {
    const response = await fetch(`${apiClient['baseUrl']}/assets/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
      },
      body: JSON.stringify({ tenantId, format, filters }),
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }
}

export const assetService = new AssetService();
